import { useState } from 'react';
import { useGroupTrip } from '../hooks/useGroupTrip';
import { Button } from './ui/Button';
import { Modal } from './ui/Modal';
import type { GroupMember, GroupActivity } from '../types/group';

interface GroupTripManagerProps {
  tripId: string;
  userId: string;
}

export default function GroupTripManager({ tripId, userId }: GroupTripManagerProps) {
  const {
    members, addMember, removeMember, updateMemberRole, inviteMember,
    votes, createVote, castVote, closeVote, getVoteStatus,
    budget, setBudget, contributeToBudget, allocateBudget,
    settings, updateSettings, analytics, canEdit, canManageMembers,
    activities, proposeActivity, approveActivity, getSuitableActivities
  } = useGroupTrip({ tripId, currentUserId: userId });

  const [showInvite, setShowInvite] = useState(false);
  const [inviteEmail, setInviteEmail] = useState('');
  const [inviteRole, setInviteRole] = useState<GroupMember['role']>('viewer');
  const [showBudget, setShowBudget] = useState(false);
  const [showSettings, setShowSettings] = useState(false);
  const [newBudget, setNewBudget] = useState(0);
  const [contribution, setContribution] = useState(0);

  const handleInvite = async () => {
    if (!inviteEmail) return;

    const code = await inviteMember(inviteEmail, inviteRole);
    // In real app, send email with invite code
    console.log('Invite code:', code);

    setInviteEmail('');
    setShowInvite(false);
  };

  const handleSetBudget = async () => {
    await setBudget(newBudget);
    setNewBudget(0);
    setShowBudget(false);
  };

  const handleContribute = async () => {
    await contributeToBudget(contribution);
    setContribution(0);
  };

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h2 className="text-xl font-bold text-gray-900">Group Trip Manager</h2>
          <p className="text-gray-500">{members.length} travelers</p>
        </div>
        {canManageMembers && (
          <Button variant="primary" onClick={() => setShowInvite(true)}>
            + Invite Traveler
          </Button>
        )}
      </div>

      {/* Members */}
      <div className="bg-white rounded-lg shadow p-6">
        <h3 className="text-lg font-semibold mb-4">Travelers</h3>

        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
          {members.map((member: GroupMember) => (
            <div key={member.id} className="flex items-center gap-3 p-3 bg-gray-50 rounded-lg">
              <div className="w-10 h-10 rounded-full bg-primary-100 flex items-center justify-center text-primary-700 font-bold">
                {member.displayName.charAt(0).toUpperCase()}
              </div>
              <div className="flex-1 min-w-0">
                <p className="font-medium truncate">{member.displayName}</p>
                <p className="text-sm text-gray-500 truncate">{member.email}</p>
              </div>
              <div className="flex items-center gap-1">
                <span className={`text-xs px-2 py-1 rounded-full ${
                  member.role === 'owner' ? 'bg-amber-100 text-amber-700' :
                  member.role === 'admin' ? 'bg-blue-100 text-blue-700' :
                  member.role === 'editor' ? 'bg-green-100 text-green-700' :
                  'bg-gray-100 text-gray-700'
                }`}>
                  {member.role}
                </span>
                {canManageMembers && member.role !== 'owner' && (
                  <button
                    onClick={() => removeMember(member.id)}
                    className="p-1 text-red-500 hover:bg-red-100 rounded"
                  >
                    ✕
                  </button>
                )}
              </div>
            </div>
          ))}

          {members.length === 0 && (
            <div className="col-span-full text-center py-8">
              <p className="text-gray-500 mb-4">No travelers yet</p>
              <Button variant="primary" onClick={() => setShowInvite(true)}>
                Invite Your First Traveler
              </Button>
            </div>
          )}
        </div>
      </div>

      {/* Budget & Activities Row */}
      <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
        {/* Budget Tracker */}
        <div className="bg-white rounded-lg shadow p-6">
          <div className="flex items-center justify-between mb-4">
            <h3 className="text-lg font-semibold">💰 Budget</h3>
            <Button variant="ghost" size="sm" onClick={() => setShowBudget(true)}>
              Setup
            </Button>
          </div>

          {budget ? (
            <div>
              <div className="mb-4">
                <div className="flex items-center justify-between text-sm mb-1">
                  <span className="text-gray-500">Spent</span>
                  <span className="font-medium">
                    ${budget.spent} / ${budget.totalBudget}
                  </span>
                </div>
                <div className="w-full bg-gray-200 rounded-full h-2">
                  <div
                    className="bg-primary-500 h-2 rounded-full transition-all"
                    style={{ width: `${(budget.spent / budget.totalBudget) * 100}%` }}
                  />
                </div>
              </div>

              {/* Contribution */}
              <div className="border-t pt-4">
                <p className="text-sm font-medium mb-2">Contribute to Budget</p>
                <div className="flex gap-2">
                  <input
                    type="number"
                    value={contribution}
                    onChange={(e) => setContribution(Number(e.target.value))}
                    placeholder="Amount"
                    className="flex-1 px-3 py-2 border rounded-lg"
                  />
                  <Button variant="primary" size="sm" onClick={handleContribute}>
                    Add
                  </Button>
                </div>
              </div>
            </div>
          ) : (
            <div className="text-center py-6">
              <p className="text-gray-500 mb-4">No budget set</p>
              <Button variant="outline" onClick={() => setShowBudget(true)}>
                Set Budget
              </Button>
            </div>
          )}
        </div>

        {/* Activities */}
        <div className="bg-white rounded-lg shadow p-6">
          <div className="flex items-center justify-between mb-4">
            <h3 className="text-lg font-semibold">🎯 Activities</h3>
            <Button variant="ghost" size="sm">
              Propose
            </Button>
          </div>

          <div className="space-y-3">
            {activities.slice(0, 3).map((activity: GroupActivity) => (
              <div key={activity.id} className="flex items-center justify-between p-2 bg-gray-50 rounded">
                <div>
                  <p className="font-medium">{activity.title}</p>
                  <p className="text-sm text-gray-500">${activity.estimatedCost}</p>
                </div>
                <span className={`text-xs px-2 py-1 rounded-full ${
                  activity.status === 'approved' ? 'bg-green-100 text-green-700' :
                  activity.status === 'proposed' ? 'bg-yellow-100 text-yellow-700' :
                  'bg-gray-100 text-gray-700'
                }`}>
                  {activity.status}
                </span>
              </div>
            ))}

            {activities.length === 0 && (
              <p className="text-gray-500 text-center py-4">No activities proposed yet</p>
            )}
          </div>
        </div>
      </div>

      {/* Settings */}
      <div className="bg-white rounded-lg shadow p-6">
        <div className="flex items-center justify-between mb-4">
          <h3 className="text-lg font-semibold">⚙️ Trip Settings</h3>
          {canEdit && (
            <Button variant="ghost" size="sm" onClick={() => setShowSettings(true)}>
              Edit
            </Button>
          )}
        </div>

        <div className="grid grid-cols-2 md:grid-cols-4 gap-4 text-sm">
          <div>
            <p className="text-gray-500">Kid-Friendly</p>
            <p className="font-medium">{settings?.kidFriendlyMode ? 'Yes' : 'No'}</p>
          </div>
          <div>
            <p className="text-gray-500">Voting Required</p>
            <p className="font-medium">{settings?.votingRequired ? 'Yes' : 'No'}</p>
          </div>
          <div>
            <p className="text-gray-500">Activity Level</p>
            <p className="font-medium capitalize">{settings?.activityLevel || 'N/A'}</p>
          </div>
          <div>
            <p className="text-gray-500">Transport</p>
            <p className="font-medium">
              {settings?.transportPreferences?.privateTransport ? 'Private' : 'Mixed'}
            </p>
          </div>
        </div>
      </div>

      {/* Analytics */}
      {analytics && (
        <div className="bg-white rounded-lg shadow p-6">
          <h3 className="text-lg font-semibold mb-4">📊 Group Activity</h3>
          <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
            <div className="text-center">
              <p className="text-2xl font-bold text-primary-600">{analytics.activeMembers}</p>
              <p className="text-sm text-gray-500">Active Members</p>
            </div>
            <div className="text-center">
              <p className="text-2xl font-bold text-green-600">{analytics.activitiesApproved}</p>
              <p className="text-sm text-gray-500">Activities Booked</p>
            </div>
            <div className="text-center">
              <p className="text-2xl font-bold text-blue-600">{analytics.totalVotes}</p>
              <p className="text-sm text-gray-500">Votes Cast</p>
            </div>
            <div className="text-center">
              <p className="text-2xl font-bold text-amber-600">{analytics.mostActiveMember}</p>
              <p className="text-sm text-gray-500">Most Active</p>
            </div>
          </div>
        </div>
      )}

      {/* Invite Modal */}
      <Modal isOpen={showInvite} onClose={() => setShowInvite(false)} title="Invite Traveler">
        <div className="space-y-4">
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">Email</label>
            <input
              type="email"
              value={inviteEmail}
              onChange={(e) => setInviteEmail(e.target.value)}
              placeholder="traveler@example.com"
              className="w-full px-3 py-2 border rounded-lg focus:ring-2 focus:ring-primary-500"
            />
          </div>
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">Role</label>
            <select
              value={inviteRole}
              onChange={(e) => setInviteRole(e.target.value as GroupMember['role'])}
              className="w-full px-3 py-2 border rounded-lg focus:ring-2 focus:ring-primary-500"
            >
              <option value="viewer">Viewer - Can view but not edit</option>
              <option value="editor">Editor - Can edit itinerary</option>
              <option value="admin">Admin - Can manage members and settings</option>
            </select>
          </div>
          <div className="flex justify-end gap-3 pt-4">
            <Button variant="outline" onClick={() => setShowInvite(false)}>Cancel</Button>
            <Button variant="primary" onClick={handleInvite} disabled={!inviteEmail}>
              Send Invite
            </Button>
          </div>
        </div>
      </Modal>

      {/* Budget Modal */}
      <Modal isOpen={showBudget} onClose={() => setShowBudget(false)} title="Set Budget">
        <div className="space-y-4">
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">Total Budget ($)</label>
            <input
              type="number"
              value={newBudget}
              onChange={(e) => setNewBudget(Number(e.target.value))}
              placeholder="5000"
              className="w-full px-3 py-2 border rounded-lg focus:ring-2 focus:ring-primary-500"
            />
          </div>
          <div className="flex justify-end gap-3 pt-4">
            <Button variant="outline" onClick={() => setShowBudget(false)}>Cancel</Button>
            <Button variant="primary" onClick={handleSetBudget} disabled={!newBudget}>
              Set Budget
            </Button>
          </div>
        </div>
      </Modal>

      {/* Settings Modal */}
      <Modal isOpen={showSettings} onClose={() => setShowSettings(false)} title="Trip Settings">
        <div className="space-y-4">
          <div className="flex items-center justify-between">
            <span className="text-gray-700">Kid-Friendly Mode</span>
            <button
              onClick={() => updateSettings({ kidFriendlyMode: !settings?.kidFriendlyMode })}
              className={`w-12 h-6 rounded-full transition-colors ${
                settings?.kidFriendlyMode ? 'bg-primary-500' : 'bg-gray-300'
              }`}
            >
              <div className={`w-5 h-5 rounded-full bg-white transition-transform ${
                settings?.kidFriendlyMode ? 'translate-x-6' : 'translate-x-0.5'
              }`} />
            </button>
          </div>
          <div className="flex items-center justify-between">
            <span className="text-gray-700">Require Approval for Activities</span>
            <button
              onClick={() => updateSettings({ requireApproval: !settings?.requireApproval })}
              className={`w-12 h-6 rounded-full transition-colors ${
                settings?.requireApproval ? 'bg-primary-500' : 'bg-gray-300'
              }`}
            >
              <div className={`w-5 h-5 rounded-full bg-white transition-transform ${
                settings?.requireApproval ? 'translate-x-6' : 'translate-x-0.5'
              }`} />
            </button>
          </div>
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">Activity Level</label>
            <select
              value={settings?.activityLevel || 'balanced'}
              onChange={(e) => updateSettings({ activityLevel: e.target.value as any })}
              className="w-full px-3 py-2 border rounded-lg"
            >
              <option value="relaxed">Relaxed - Easy pace</option>
              <option value="balanced">Balanced - Mix of activities</option>
              <option value="active">Active - Lots of activities</option>
            </select>
          </div>
          <div className="flex justify-end gap-3 pt-4">
            <Button variant="primary" onClick={() => setShowSettings(false)}>
              Save Settings
            </Button>
          </div>
        </div>
      </Modal>
    </div>
  );
}
